import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../components/Breadcrumbs";
import ScrollToTopButton from "../components/ScrollToTopButton";
import SetCard from "../components/SetCard";
import { listSets } from "../lib/api";
import { setsListMeta } from "../lib/seo";

export default function SetsListPage() {
  const [sets, setSets] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setState("loading");
    listSets()
      .then((data) => {
        if (!alive) return;
        setSets(data);
        setState("ready");
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Failed to load sets");
        setState("error");
      });
    return () => {
      alive = false;
    };
  }, []);

  const meta = setsListMeta();

  return (
    <div
      className="mx-auto w-full max-w-[1400px] px-5 py-8 md:px-gutter md:py-10"
      data-testid="sets-list-page"
    >
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="robots" content={meta.robots} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:site_name" content="CardCapture" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content={meta.ogType} />
        <meta property="og:image" content={meta.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.ogImage} />
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Pokémon", to: "/pokemon/en/sets" },
          { label: "English" },
        ]}
      />

      <div className="mt-6 flex items-baseline justify-between">
        <h1 className="text-title-page text-ink-text">
          Pokémon <span className="text-ink-muted"> · English</span>
        </h1>
        <div className="text-meta text-ink-muted tabular" data-testid="sets-count">
          {state === "ready" ? `${sets.length} sets` : "…"}
        </div>
      </div>
      <p className="mt-2 max-w-[62ch] text-body text-ink-muted">
        All English Pokémon sets, newest release first. Card counts and
        release dates come from TCGdex; images stream from the CardCapture
        cache when available.
      </p>

      {state === "loading" && (
        <div className="mt-16 text-body text-ink-muted" data-testid="sets-loading">
          Loading sets…
        </div>
      )}

      {state === "error" && (
        <div className="mt-16 text-body text-ink-muted" data-testid="sets-error">
          Couldn't load sets: {error}
        </div>
      )}

      {state === "ready" && sets.length === 0 && (
        <div
          className="mt-16 rounded-card border border-ink-chip bg-ink-surface p-10 text-body text-ink-muted"
          data-testid="sets-empty"
        >
          No sets have been imported yet. The catalog sync is either running
          or hasn't been triggered by an admin.
        </div>
      )}

      {state === "ready" && sets.length > 0 && (
        <div
          data-testid="sets-grid"
          className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {sets.map((s) => (
            <SetCard key={s.id} set={s} />
          ))}
        </div>
      )}
      <ScrollToTopButton />
    </div>
  );
}
